import { useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { FileX, Building2, Receipt, Banknote, ClipboardCheck, Clock, AlertCircle, CheckCircle2, XCircle, Mail } from "lucide-react";
import { SubmissionLayout, DownloadActions, StatusBadge } from "../../components/SubmissionLayout";
import BonsaiMate from "../../components/BonsaiMate";

function FinancialRow({ label, value, accent = false }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-white/10/40 last:border-0">
      <span className="text-sm text-txt-faint">{label}</span>
      <span className={`text-sm font-medium ${accent ? 'text-teal' : 'text-txt-faint'}`}>
        {value || "\u2014"}
      </span>
    </div>
  );
}

function StatusField({ label, status }) {
  const fmt = (s) => {
    if (s === "done_na") return { text: "Done / N/A", color: "text-teal bg-teal-dim border-teal", icon: CheckCircle2 };
    if (s === "to_do") return { text: "To do", color: "text-status-amber bg-status-amber-dim border-amber-200", icon: AlertCircle };
    if (s === "risk") return { text: "RISK", color: "text-status-red bg-status-red-dim border-red-200", icon: XCircle };
    return { text: s || "\u2014", color: "text-slate-600 bg-slate-50 border-slate-200", icon: null };
  };
  const { text, color, icon: StatusIcon } = fmt(status);
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-white/10/40 last:border-0">
      <span className="text-sm text-txt-faint">{label}</span>
      <span className={`text-xs font-medium flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${color}`}>
        {StatusIcon && <StatusIcon className="w-3.5 h-3.5" />}
        {text}
      </span>
    </div>
  );
}

// BM-H3768: closures made since 6 Oct 2026 carry what the server read from the
// ledger and compliance records, the closer's reasons, five confirmations and
// what happened to the closure email. Older closures keep the old checklist.
const CHECK_STYLE = {
  ok: "text-teal bg-teal-dim border-teal",
  attention: "text-status-amber bg-status-amber-dim border-amber-200",
  unknown: "text-status-amber bg-status-amber-dim border-amber-200",
  blocked: "text-status-red bg-status-red-dim border-red-200",
};
const CHECK_WORD = { ok: "Clear", attention: "Explained", unknown: "Not readable", blocked: "Blocked" };

function Panel({ title, icon: Icon, testid, children }) {
  return (
    <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
        <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans"><Icon className="w-4 h-4" /> {title}</h2>
      </div>
      <div className="p-5 space-y-1">{children}</div>
    </div>
  );
}

export function ClosureFromRecords({ data, residualActionLabels }) {
  const rec = data.closure_records || {};
  const notes = data.closure_explanations || {};
  const confirms = data.closure_confirmations || {};
  const email = data.closure_email_result || {};
  const row = (c) => (
    <div key={c.key} className="py-2.5 border-b border-white/10/40 last:border-0" data-testid={`closure-record-${c.key}`}>
      <div className="flex justify-between items-center gap-3">
        <span className="text-sm text-txt-faint">{c.label}</span>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-md border ${CHECK_STYLE[c.state] || CHECK_STYLE.unknown}`}>{CHECK_WORD[c.state] || c.state}</span>
      </div>
      <p className="text-xs text-txt-muted mt-1">{c.detail}</p>
      {notes[c.key] && <p className="text-xs text-txt mt-1">Reason given: {notes[c.key]}</p>}
    </div>
  );
  const checks = rec.checks || [];
  return (
    <>
      <Panel title="Ledger (read from CaseMinder)" icon={Banknote} testid="section-ledger-records">
        {checks.filter(c => c.group === "ledger").map(row)}
        <FinancialRow label="Ledger balance given at closure" value={data.declared_ledger_balance} accent />
        {notes.ledger_difference && <p className="text-xs text-txt mt-1">Why it differs: {notes.ledger_difference}</p>}
        {data.has_residual_balance === "yes" && (
          <div className="mt-3 p-3.5 bg-status-amber-dim/60 rounded-md border border-amber-100 space-y-1">
            <FinancialRow label="Reason money is still held" value={data.residual_balance_reason} />
            <FinancialRow label="Plan" value={residualActionLabels[data.residual_balance_action] || data.residual_balance_action} />
            <FinancialRow label="Target date" value={data.residual_balance_target_date} />
          </div>
        )}
      </Panel>
      <Panel title="Compliance (read from CaseMinder)" icon={ClipboardCheck} testid="section-compliance-records">
        {checks.filter(c => c.group === "compliance").map(row)}
        {rec.retention && (
          <div className="py-2.5" data-testid="closure-record-retention">
            <span className="text-sm text-txt-faint">Keep the file for {rec.retention.years} years</span>
            <p className="text-xs text-txt-muted mt-1">{rec.retention.basis}</p>
          </div>
        )}
      </Panel>
      <Panel title="Confirmed by the closer" icon={CheckCircle2} testid="section-confirmations">
        {Object.keys(confirms).map(k => (
          <StatusField key={k} label={k.replace(/_/g, " ")} status={confirms[k] === true ? "done_na" : "to_do"} />
        ))}
        <p className="text-xs text-txt-muted pt-2">Records read {rec.read_at ? new Date(rec.read_at).toLocaleString("en-GB") : ""}.</p>
      </Panel>
      <Panel title="Closure email to the client" icon={Mail} testid="section-closure-email">
        <FinancialRow label="Outcome" value={email.status === "sent" ? "Sent" : email.status === "not_sent" ? "Not sent" : email.status ? `Failed (${email.status})` : "Not recorded"} accent={email.status === "sent"} />
        {email.to && <FinancialRow label="To" value={email.to.join(", ")} />}
        {email.at && <FinancialRow label="When" value={new Date(email.at).toLocaleString("en-GB")} />}
        {email.reason && <FinancialRow label="Reason" value={email.reason} />}
        {email.error && <p className="text-xs text-status-red mt-1">{email.error}</p>}
        {data.closure_email?.body && <pre className="mt-2 text-xs text-txt-faint whitespace-pre-wrap font-sans">{data.closure_email.body}</pre>}
      </Panel>
      <SharePointArchivePanel matterReference={data.matter_reference} initial={data.sharepoint_archive} />
    </>
  );
}

// BM-H3768: the SharePoint folder is renamed "CLOSED - ..." and moved into the
// practice area's CLOSED VIA CM folder when the file closes.
export function SharePointArchivePanel({ matterReference, initial }) {
  const [sp, setSp] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/file-closure/${encodeURIComponent(matterReference)}/archive-sharepoint`);
      setSp(res.data || {});
    } catch (e) {
      setSp({ status: "error", error: errorMessage(e, "Could not reach SharePoint") });
    } finally { setBusy(false); }
  };
  const label = sp.status === "archived" ? "Renamed and archived" : sp.status === "not_found" ? "Folder not found"
    : sp.status === "not_done" ? "Not changed" : sp.status === "error" ? "Failed" : "Not recorded";
  return (
    <Panel title="SharePoint folder" icon={FileX} testid="section-sharepoint-archive">
      <FinancialRow label="Outcome" value={label} accent={sp.status === "archived"} />
      {sp.folder_name && <FinancialRow label="Folder" value={sp.folder_name} />}
      {sp.archive && <FinancialRow label="Moved to" value={sp.archive} />}
      {(sp.reason || sp.error) && <p className={`text-xs mt-1 ${sp.status === "error" ? "text-status-red" : "text-txt-muted"}`}>{sp.reason || sp.error}</p>}
      {sp.web_url && <a href={sp.web_url} target="_blank" rel="noreferrer" className="text-xs text-teal underline">Open the folder</a>}
      {matterReference && sp.status !== "archived" && (
        <button type="button" onClick={retry} disabled={busy} className="mt-2 text-xs px-3 py-1.5 rounded-md border border-teal text-teal" data-testid="sharepoint-archive-retry">
          {busy ? "Trying..." : "Try again"}
        </button>
      )}
    </Panel>
  );
}

export default function FileClosureDetail({ submission, formatDate, handleDownloadPDF, handleExportJson, downloadingPdf, navigate, isDirector, onDelete }) {
  const data = submission?.data || {};

  const residualActionLabels = {
    "trace_client": "Further attempts to trace the client",
    "charity": "Pay to charity under residual balance policy",
    "hold_pending": "Hold pending further instructions",
    "other": "Other (see notes)"
  };

  return (
    <>
    <SubmissionLayout
      navigate={navigate}
      title={data.matter_reference || "File Closure"}
      subtitle={data.client_name}
      matterReference={data.matter_reference}
      badge="File Closure"
      badgeColor="rose"
      badgeIcon={FileX}
      isTest={data.is_test_submission}
      isDirector={isDirector}
      onDelete={onDelete}
      actions={<DownloadActions handleDownloadPDF={handleDownloadPDF} handleExportJson={handleExportJson} downloadingPdf={downloadingPdf} />}
    >
      {/* Status Row */}
      <div className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 px-5 py-3.5 shadow-sm" data-testid="closure-header-info">
        <div className="flex items-center gap-4">
          <StatusBadge status={submission.status} />
          <span className="text-sm text-txt-faint">Submitted {formatDate(submission.created_at)}</span>
        </div>
        <span className="text-xs text-txt-muted font-mono tracking-wide">{submission.id?.slice(0, 8)}</span>
      </div>

      {/* Closure Hero */}
      <div className="bg-gradient-to-r from-rose-50 to-rose-100/80 rounded-md border-2 border-rose-200 p-6 text-center" data-testid="closure-hero">
        <div className="flex items-center justify-center gap-3 mb-2">
          <FileX className="w-8 h-8 text-rose-600" />
          <div className="text-2xl font-bold text-rose-700">Matter Closed</div>
        </div>
        <div className="text-rose-600">{data.client_name} {"\u2022"} {data.matter_reference}</div>
        <div className="text-sm text-rose-500 mt-1">Fee Earner: {data.responsible_fee_earner}</div>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="closure-bento-grid">

        {/* Matter Details — 6 cols */}
        <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-matter-details">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Building2 className="w-4 h-4" /> Matter Details
            </h2>
          </div>
          <div className="p-5 space-y-1">
            <FinancialRow label="Client Name" value={data.client_name} accent />
            <FinancialRow label="Matter Reference" value={data.matter_reference} accent />
            <FinancialRow label="Responsible Fee Earner" value={data.responsible_fee_earner} />
            <FinancialRow label="Form Completer" value={data.form_completer || "Same as fee earner"} />
            <FinancialRow label="Completer Email" value={data.completer_email} />
            <FinancialRow label="Completion Date" value={data.completion_date || formatDate(submission.created_at)} />
          </div>
        </div>

        {data.closure_records ? (
          <ClosureFromRecords data={data} residualActionLabels={residualActionLabels} />
        ) : (<>
        {/* Billing Confirmation — 6 cols */}
        <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-billing">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Receipt className="w-4 h-4" /> Billing Confirmation
            </h2>
          </div>
          <div className="p-5 space-y-1">
            <StatusField label="All agreed work billed or written off" status={data.billing_wip} />
            <StatusField label="Disbursements billed or written off" status={data.billing_disbursements} />
            <StatusField label="Outstanding invoices/credits addressed" status={data.billing_invoices || data.billing_complete} />
            <StatusField label="Time recording complete" status={data.billing_time || data.billing_external} />
          </div>
        </div>

        {/* Client Account & Residual — 6 cols */}
        <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-client-account">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Banknote className="w-4 h-4" /> Client Account & Residual Balances
            </h2>
          </div>
          <div className="p-5 space-y-1">
            <StatusField label="Client money returned/transferred" status={data.client_money_returned || data.client_account_zero} />
            <StatusField label="Residual balances dealt with per SRA rules" status={data.residual_balances_dealt || data.monies_held_long_term} />
            {data.has_residual_balance === "yes" ? (
              <div className="mt-3 p-3.5 bg-status-amber-dim/60 rounded-md border border-amber-100 space-y-1">
                <FinancialRow label="Residual balance exists" value="Yes" />
                <FinancialRow label="Amount" value={data.residual_balance_amount} accent />
                <FinancialRow label="Reason funds remain" value={data.residual_balance_reason} />
                <FinancialRow label="Proposed action" value={residualActionLabels[data.residual_balance_action] || data.residual_balance_action} />
                <FinancialRow label="Target date" value={data.residual_balance_target_date} />
              </div>
            ) : data.client_account_zero === "no" ? (
              <div className="mt-3 p-3.5 bg-status-amber-dim/60 rounded-md border border-amber-100 space-y-1">
                <FinancialRow label="Account at zero" value="No" />
                <FinancialRow label="Funds action" value={
                  data.client_account_action === "return_client" ? "Return to client immediately"
                  : data.client_account_action === "return_no_contact" ? "Return to client (cannot contact)"
                  : data.client_account_action === "send_new_solicitors" ? "Send to new solicitors"
                  : data.client_account_action
                } />
              </div>
            ) : (
              <div className="mt-2 text-xs text-teal flex items-center gap-1.5 p-2 bg-teal-dim rounded-md">
                <CheckCircle2 className="w-3.5 h-3.5" /> No residual balance
              </div>
            )}
          </div>
        </div>

        {/* Compliance Checklist — 6 cols */}
        <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-compliance">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <ClipboardCheck className="w-4 h-4" /> Compliance Checklist
            </h2>
          </div>
          <div className="p-5 space-y-1">
            <StatusField label="Undertakings given discharged?" status={data.compliance_undertakings_given} />
            <StatusField label="Undertakings received discharged?" status={data.compliance_undertakings_received} />
            <StatusField label="File in good order for archiving?" status={data.compliance_file_order} />
            <StatusField label="Client advised on scope?" status={data.compliance_client_advised} />
            <StatusField label="Scope of work completed?" status={data.compliance_scope_complete} />
            <StatusField label="Conflict checks completed?" status={data.compliance_conflicts_cleared} />
            <StatusField label="AML/KYC checks completed?" status={data.compliance_aml_complete} />
            <StatusField label="Keys/documents returned?" status={data.compliance_keys_returned} />
            <StatusField label="Retainer terminated in writing?" status={data.compliance_retainer_terminated} />
          </div>
        </div>
        </>)}
      </div>
    </SubmissionLayout>
    {data.matter_reference && (
      <BonsaiMate context="matter" matterReference={data.matter_reference} />
    )}
    </>
  );
}
